/**
 * Severity — a labeled severity indicator (Claude Design `.sev` / `.sev-dot`).
 *
 * A small rounded-square dot followed by the level label. `critical` glows;
 * `high` uses an amber-orange that has no token (kept as a literal per the
 * design); `medium` is warn; `low` is muted with a faint dot.
 *
 *   <Severity level="critical" />        → ● CRITICAL
 *   <Severity level="high">High risk</Severity>
 */
import type { HTMLAttributes, ReactElement, ReactNode } from 'react';

import { cn } from './cn.js';

export type SeverityLevel = 'critical' | 'high' | 'medium' | 'low';

export interface SeverityProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  level: SeverityLevel;
  /** Override the label (defaults to the capitalized level). */
  children?: ReactNode;
}

const levelClasses: Record<SeverityLevel, { text: string; dot: string }> = {
  critical: {
    text: 'text-danger',
    dot: 'bg-danger shadow-[0_0_7px_var(--hl-danger)]',
  },
  // amber-orange has no semantic token; design uses #ff8a3d for `high`.
  high: { text: 'text-[#ff8a3d]', dot: 'bg-[#ff8a3d]' },
  medium: { text: 'text-warn', dot: 'bg-warn' },
  low: { text: 'text-muted', dot: 'bg-faint' },
};

const defaultLabel: Record<SeverityLevel, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

export function Severity({
  level,
  children,
  className,
  ...props
}: SeverityProps): ReactElement {
  const styles = levelClasses[level];
  return (
    <span
      data-severity={level}
      className={cn(
        'inline-flex items-center gap-[7px] text-xs font-bold',
        styles.text,
        className,
      )}
      {...props}
    >
      <span
        aria-hidden="true"
        className={cn('h-2 w-2 flex-shrink-0 rounded-[2.5px]', styles.dot)}
      />
      {children ?? defaultLabel[level]}
    </span>
  );
}
